import { Button, Toggle, TooltipV2 } from '@cimplico/components';
import { ChevronLeftIcon } from '@heroicons/react/24/solid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import { UserOverview } from '../../types/user';
import { useFormContext, Controller } from 'react-hook-form';
import { WorkpaperRoleEnum } from '../../types/workpaper';
import { SubscriptionTier } from '../../types/firm';
import UpgradeHint from './UpgradeHint';
import WorkpaperRoleSelect from '../../components/user-select/WorkpaperRoleSelect';
import { useHasFeatureAccess } from '../../hooks/use-has-feature-access';

type NewWorkpaperStepThreeProps = {
    onPrev: () => void;
    onNext: () => void;
    onSubmit: () => void;
    isSubmitting: boolean;
};

function NewWorkpaperStepThree({ onPrev, onNext, onSubmit, isSubmitting }: NewWorkpaperStepThreeProps): JSX.Element {
    const navigate = useNavigate();
    const { shortId, subscriptionVersion } = useFirmContext();
    const {
        watch,
        handleSubmit,
        trigger,
        formState: { errors },
        control,
    } = useFormContext();

    const featureAccess = {
        canAssignMultipleApprovers: useHasFeatureAccess('workpaper-assign-multiple-approvers:write'),
        canAssignMultipleReviewers: useHasFeatureAccess('workpaper-assign-multiple-reviewers:write'),
        canAssignMultiplePreparers: useHasFeatureAccess('workpaper-assign-multiple-preparers:write'),
        canTogglePrivate: useHasFeatureAccess('workpaper-toggle-private:write'),
    };

    const entity = watch('entity');
    const approvers = watch('approvers') as UserOverview[];
    const reviewers = watch('reviewers') as UserOverview[];
    const preparers = watch('preparers') as UserOverview[];

    const [params] = useSearchParams();
    const returnTo = params.get('returnTo');

    function handleCancel(): void {
        navigate(returnTo || `/${shortId}/workpapers`);
    }

    return (
        <fieldset disabled={isSubmitting}>
            <form className="flex flex-row" onSubmit={handleSubmit(entity.integrations.fyi ? onNext : onSubmit)}>
                <div className="mr-4">
                    <Button leftIcon={<ChevronLeftIcon />} onClick={(): void => onPrev()} size="md" type="hollow" disabled={isSubmitting} />
                </div>
                <div className="flex flex-col gap-8 w-full">
                    <p className="text-2xl">{entity.name}</p>

                    <div className="flex flex-col gap-8 max-w-[398px]">
                        {/* APPROVERS */}
                        <Controller
                            name="approvers"
                            rules={{ required: 'An approver is required.' }}
                            control={control}
                            render={({ field: { ref, onChange, value } }): JSX.Element => (
                                <div>
                                    <WorkpaperRoleSelect
                                        role={WorkpaperRoleEnum.APPROVER}
                                        innerRef={ref}
                                        isMulti={featureAccess.canAssignMultipleApprovers}
                                        errorMessage={errors.approvers?.message?.toString()}
                                        defaultValue={value}
                                        title="Approvers"
                                        placeholder="Select approver(s)..."
                                        disabledUsers={[...preparers, ...reviewers]}
                                        onChange={(approvers): void => {
                                            onChange(approvers);
                                            trigger('approvers');
                                        }}
                                    />
                                    {subscriptionVersion === 1 && (
                                        <UpgradeHint plan={SubscriptionTier.PROFESSIONAL} message="assign multiple approvers" />
                                    )}
                                </div>
                            )}
                        />
                        {/* REVIEWERS */}
                        <Controller
                            name="reviewers"
                            control={control}
                            render={({ field: { ref, onChange, value } }): JSX.Element => (
                                <div>
                                    <WorkpaperRoleSelect
                                        role={WorkpaperRoleEnum.REVIEWER}
                                        innerRef={ref}
                                        isOptional
                                        isMulti={featureAccess.canAssignMultipleReviewers}
                                        disabledUsers={approvers ? [...approvers, ...preparers] : preparers}
                                        errorMessage={errors.reviewers?.message?.toString()}
                                        defaultValue={value}
                                        title="Reviewers"
                                        placeholder="Select reviewer(s)..."
                                        onChange={(reviewers): void => {
                                            onChange(reviewers);
                                            trigger('reviewers');
                                        }}
                                    />
                                    {subscriptionVersion === 1 && (
                                        <UpgradeHint plan={SubscriptionTier.ESSENTIAL} message="assign multiple reviewers" />
                                    )}
                                </div>
                            )}
                        />
                        {/* PREPARERS */}
                        <Controller
                            name="preparers"
                            control={control}
                            render={({ field: { ref, onChange, value } }): JSX.Element => (
                                <div>
                                    <WorkpaperRoleSelect
                                        role={WorkpaperRoleEnum.PREPARER}
                                        innerRef={ref}
                                        isOptional
                                        isMulti={featureAccess.canAssignMultiplePreparers}
                                        disabledUsers={approvers ? [...approvers, ...reviewers] : reviewers}
                                        errorMessage={errors.preparers?.message?.toString()}
                                        defaultValue={value}
                                        title="Preparers"
                                        placeholder="Select preparer(s)..."
                                        onChange={(preparers): void => {
                                            onChange(preparers);
                                            trigger('preparers');
                                        }}
                                    />
                                    {subscriptionVersion === 1 && (
                                        <UpgradeHint plan={SubscriptionTier.ESSENTIAL} message="assign multiple preparers" />
                                    )}
                                </div>
                            )}
                        />
                        {/* ACCESS */}
                        <div>
                            <p className="block text-sm font-medium text-gray-700 mb-1">Access Control</p>
                            <Controller
                                name="private"
                                control={control}
                                render={({ field: { onChange, value } }): JSX.Element => {
                                    return (
                                        <TooltipV2
                                            message="Please upgrade your plan to enable this feature."
                                            disabled={featureAccess.canTogglePrivate}
                                        >
                                            <Toggle
                                                onToggle={onChange}
                                                label="Private"
                                                defaultEnabled={value}
                                                isDisabled={!featureAccess.canTogglePrivate}
                                            />
                                        </TooltipV2>
                                    );
                                }}
                            />

                            <label className="mt-2 text-sm text-gray-500">* Only assignees will have access to this workpaper.</label>
                            {subscriptionVersion === 1 && (
                                <UpgradeHint plan={SubscriptionTier.ESSENTIAL} message="make workpapers private" />
                            )}
                            <div className="mt-4 mb-2">
                                <Controller
                                    name="permissions.preparer.canPostJournal"
                                    control={control}
                                    render={({ field: { onChange, value } }): JSX.Element => {
                                        return (
                                            <Toggle onToggle={onChange} label="Allow preparers to post journals" defaultEnabled={value} />
                                        );
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    {/* FORM CANCEL + NEXT STEP */}
                    <div className="flex justify-end gap-4">
                        <Button type="hollow" label="Cancel" onClick={handleCancel} disabled={isSubmitting} />
                        {entity.integrations.fyi ? (
                            <Button type="primary" label="Next" isSubmit />
                        ) : (
                            <Button
                                type="primary"
                                isSubmit
                                label={isSubmitting ? 'Creating...' : 'Create Workpaper'}
                                disabled={isSubmitting}
                                isLoading={isSubmitting}
                            />
                        )}
                    </div>
                </div>
            </form>
        </fieldset>
    );
}

export default NewWorkpaperStepThree;
